/**
 * Default configuration for the IntersectionObserver.
 * - threshold: Trigger callback when 10% of the element is visible
 * - rootMargin: Start observing 50px before the element enters the viewport
 */
const DEFAULT_OBSERVER_OPTIONS: IntersectionObserverInit = {
  threshold: 0.1,
  rootMargin: '50px',
};

/**
 * Callback function signature for widget visibility changes.
 * Called when a widget enters the viewport.
 */
type OnWidgetVisibleCallback = (widgetId: string) => void;

/**
 * ObserverManager - Manages lazy-loading of widgets using IntersectionObserver.
 *
 * This singleton class tracks widget elements and triggers callbacks when they
 * become visible in the viewport. Used primarily for lazy-loading widget data
 * only when the widget scrolls into view.
 *
 * @example
 * ```ts
 * observerManager.observe('widget-123', elementRef, (widgetId) => {
 *   fetchWidgetData(widgetId);
 * });
 * ```
 */
class ObserverManager {
  /** The underlying IntersectionObserver instance */
  private intersectionObserver: IntersectionObserver | null = null;

  /** Map of widget IDs to their visibility callbacks */
  private visibilityCallbacksByWidgetId: Map<string, OnWidgetVisibleCallback> =
    new Map();

  /** Map of widget IDs to their observed DOM elements */
  private observedElementsByWidgetId: Map<string, Element> = new Map();

  /**
   * Initializes the IntersectionObserver with the given options.
   * Called automatically on first observe() call if not already initialized.
   *
   * @param observerOptions - IntersectionObserver configuration options
   */
  init(
    observerOptions: IntersectionObserverInit = DEFAULT_OBSERVER_OPTIONS,
  ): void {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((intersectionEntry) => {
        const widgetId =
          intersectionEntry.target.getAttribute('data-widget-id');

        if (!widgetId) return;

        const onVisibleCallback =
          this.visibilityCallbacksByWidgetId.get(widgetId);

        if (intersectionEntry.isIntersecting && onVisibleCallback) {
          onVisibleCallback(widgetId);
        }
      });
    }, observerOptions);
  }

  /**
   * Starts observing a widget element for viewport visibility.
   * Automatically initializes the observer if not already done.
   *
   * @param widgetId - Unique identifier for the widget
   * @param widgetElement - DOM element to observe
   * @param onVisibleCallback - Callback fired when widget becomes visible
   */
  observe(
    widgetId: string,
    widgetElement: Element,
    onVisibleCallback: OnWidgetVisibleCallback,
  ): void {
    if (!this.intersectionObserver) {
      this.init();
    }

    this.visibilityCallbacksByWidgetId.set(widgetId, onVisibleCallback);
    this.observedElementsByWidgetId.set(widgetId, widgetElement);
    this.intersectionObserver?.observe(widgetElement);
  }

  /**
   * Stops observing a specific widget and cleans up its references.
   *
   * @param widgetId - Unique identifier for the widget to stop observing
   */
  unobserve(widgetId: string): void {
    if (!this.intersectionObserver) return;

    const widgetElement = this.observedElementsByWidgetId.get(widgetId);

    if (widgetElement) {
      this.intersectionObserver.unobserve(widgetElement);
      this.observedElementsByWidgetId.delete(widgetId);
      this.visibilityCallbacksByWidgetId.delete(widgetId);
    }
  }

  /**
   * Disconnects the observer and clears all tracked widgets.
   * Call this when the widget container unmounts to prevent memory leaks.
   */
  destroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    this.visibilityCallbacksByWidgetId.clear();
    this.observedElementsByWidgetId.clear();
  }
}

/** Singleton instance of ObserverManager for application-wide widget observation */
const observerManager = new ObserverManager();

export default observerManager;
